<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { ROLE_LABELS, STAFF_ROLES } from '../api/admin.js';
import BrandGlyph from '../components/BrandGlyph.vue';
import DuoIcon, { type DuoIconName } from '../components/DuoIcon.vue';
import PlatformNoticeBar from '../components/PlatformNoticeBar.vue';
import SkinArt from '../components/SkinArt.vue';
import type { ArtKey } from '../skins/index.js';
import { useBrand } from '../stores/brand.js';
import { useSession } from '../stores/session.js';
import { useTenantStatus } from '../stores/tenant.js';
import { useViewport } from '../stores/viewport.js';
import { afterLoginPath } from '../ui/h5.js';
import { ROLE_NEED_LABELS } from '../ui/route-need.js';
import { toast } from '../ui/toast.js';
import '../styles/public-kit.css';

/**
 * 错误与状态页(设计图 24):404 · 无权限 · 维护中 · 加载失败 · 机构暂停(租户只读是横幅,不是整页)。
 * 规则:无权限页写明需要的角色与当前角色;维护不安排在考试时段,进行中的考试不受影响;加载失败给重试与错误编号。
 * 考试答题页断线不在这里处理,走考试端的续考。`?demo=1` 时底部出现四种状态切换(给验收看)。
 * 版式(2026-10-13「精修做工」第二轮):自己一整屏,不套官网的导航和页脚 —— 学生端、后台、考试端出了岔子也会跳到这里,
 * 顶着官网的菜单不合适,也少一层会出错的东西。很浅的底加一团柔和的光;顶上一个小小的品牌标;中间一张抬起的卡片:
 * 「这一页不存在」「维护中」两种是小智(拿放大镜 / 戴安全帽)从卡片上沿探出来,别的状态是一个双色图标块;
 * 然后一句话 + 说明 + 去处,卡片底栏是错误编号。自适应到手机。
 * 这是静态引入的兜底页(router.ts):里面不能有懒加载的东西,分包加载失败时它自己也要能显示。
 */
type Kind = 'lost' | 'forbidden' | 'maintenance' | 'failed' | 'suspended';
const route = useRoute();
const session = useSession();
const tenant = useTenantStatus();
const brand = useBrand('learning');
const override = ref<Kind | null>(null);
const kind = computed<Kind>(() => override.value ?? (route.meta['status'] as Kind | undefined) ?? 'lost');
const demo = computed(() => route.query['demo'] === '1');
/** 那句话上面的图:找不到 = 小智拿放大镜,维护中 = 小智戴安全帽;别的状态没有图,是一个图标块 */
const statusArt = computed<ArtKey | null>(() =>
  kind.value === 'lost' ? 'empty-search' : kind.value === 'maintenance' ? 'empty-fix' : null,
);
const STATUS_ICON: Partial<Record<Kind, DuoIconName>> = {
  forbidden: 'lock',
  failed: 'alert',
  suspended: 'clock',
};

const NEED: Record<string, string> = ROLE_NEED_LABELS;
const myRoles = computed(() => {
  const roles = session.me?.siteRoles ?? [];
  const labels = roles.map((r) => ROLE_LABELS[r] ?? r);
  return labels.length ? labels.join('、') : session.accessToken ? '学生 / 家长' : '未登录';
});
const until = computed(() => {
  const u = route.query['until'];
  if (typeof u !== 'string' || !u) return '';
  const d = new Date(u);
  return Number.isNaN(d.getTime())
    ? u
    : d.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
});

const pad = (n: number) => String(n).padStart(2, '0');
const errorNo = computed(() => {
  const d = new Date();
  const code = { lost: '404', forbidden: '403', maintenance: '503', failed: '000', suspended: '423' }[
    kind.value
  ];
  return `E${code}-${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
});
const orgName = computed(() => tenant.info?.brand?.displayName ?? tenant.info?.name ?? '机构');
const isStudent = computed(
  () => !!session.accessToken && !STAFF_ROLES.some((r) => session.me?.siteRoles.includes(r)),
);
// 状态页是公开路由,直接打开时全局守卫不加载身份:登录着的先拿一下身份,员工才显示「回工作台」
onMounted(() => {
  if (session.accessToken && !session.me) void session.loadMe().catch(() => undefined);
});

async function copyNo() {
  try {
    await navigator.clipboard.writeText(errorNo.value);
    toast('错误编号已复制,反馈时发给老师或客服', 'ok');
  } catch {
    toast(errorNo.value);
  }
}
/** 没有可回的地址时:电脑回官网首页;手机回入口分流(学生端 H5 的落点是入口页,不是官网) */
const fallback = () => afterLoginPath(undefined, useViewport().phone);
function retry() {
  const to = route.query['to'];
  // 整页重新加载:发版后拿新的分包
  window.location.assign(typeof to === 'string' && to.startsWith('/') ? to : fallback());
}
function reload() {
  const from = route.query['from'];
  window.location.assign(typeof from === 'string' && from.startsWith('/') ? from : fallback());
}
const TABS: { k: Kind; t: string }[] = [
  { k: 'lost', t: '404' },
  { k: 'forbidden', t: '无权限' },
  { k: 'maintenance', t: '维护中' },
  { k: 'failed', t: '加载失败' },
];
</script>

<template>
  <div class="ptl sx">
    <PlatformNoticeBar />
    <div class="sx-glow" aria-hidden="true"></div>
    <!-- 品牌标:只是告诉人这是谁家的页面(站点信息没取到就不出,不先露出平台的) -->
    <header v-if="brand.name || brand.logoUrl" class="sx-brand">
      <img v-if="brand.logoUrl" :src="brand.logoUrl" alt="" />
      <i v-else
        ><BrandGlyph v-if="brand.glyph" /><template v-else>{{ brand.mark }}</template></i
      >
      <b>{{ brand.name }}</b>
    </header>
    <main class="sx-main">
      <section
        class="ptl-plate still sx-card"
        :class="{ 'has-art': statusArt }"
        :aria-labelledby="`st-${kind}`"
      >
        <span v-if="statusArt" class="sx-art"><SkinArt :name="statusArt" /></span>
        <span v-else class="sx-ico" :class="{ 'is-warm': kind === 'failed' }" aria-hidden="true"
          ><DuoIcon :name="STATUS_ICON[kind] ?? 'alert'" :size="40"
        /></span>
        <template v-if="kind === 'lost'">
          <h1 :id="`st-${kind}`">这一页不存在</h1>
          <p>链接可能已失效,或者页面被移动了。</p>
          <div class="sx-acts">
            <!-- A25-4:学生(含没登录的访客,点了先登录)给「回学习地图」;员工给「回工作台」 -->
            <RouterLink
              v-if="isStudent || !session.accessToken"
              class="ptl-cta brand lg"
              :to="{ name: 'journey', query: { go: 'map' } }"
              >回学习地图</RouterLink
            >
            <RouterLink v-else-if="session.accessToken" class="ptl-cta brand lg" :to="{ name: 'home' }"
              >回工作台</RouterLink
            >
            <!-- 手机上也给这个出口:官网 2026-10-13 起是自适应的了(以前是 1440 画板,手机上不给) -->
            <RouterLink class="ptl-cta line lg" :to="{ name: 'site-home' }">回官网</RouterLink>
          </div>
        </template>

        <template v-else-if="kind === 'forbidden'">
          <h1 :id="`st-${kind}`">你没有这个页面的权限</h1>
          <p>需要「{{ NEED[String(route.query['need'] ?? '')] ?? '相应' }}」角色。你当前是:{{ myRoles }}。</p>
          <div class="sx-acts">
            <RouterLink class="ptl-cta brand lg" :to="{ name: 'home' }">回工作台</RouterLink>
            <RouterLink
              class="ptl-cta line lg"
              :to="{ name: 'login', query: { redirect: String(route.query['from'] ?? '/') } }"
              >换个账号登录</RouterLink
            >
          </div>
          <small>需要开通请联系本机构管理员分配角色。</small>
        </template>

        <template v-else-if="kind === 'suspended'">
          <h1 :id="`st-${kind}`">机构服务已暂停,请联系管理员</h1>
          <p>
            {{ orgName }}的服务暂停了,机构管理员续费后立即恢复。已发布的考试与已开放的赛事按原计划进行,
            监考、阅卷、发证照常;数据导出也照常可用。
          </p>
          <div class="sx-acts">
            <RouterLink v-if="session.canExam" class="ptl-cta brand lg" :to="{ name: 'admin-exams' }"
              >去考务</RouterLink
            >
            <RouterLink class="ptl-cta line lg" :to="{ name: 'admin-downloads' }">下载中心</RouterLink>
          </div>
          <small>学生端照常能看已有的学习记录与成绩。</small>
        </template>

        <template v-else-if="kind === 'maintenance'">
          <h1 :id="`st-${kind}`">系统维护中{{ until ? `,预计 ${until} 恢复` : '' }}</h1>
          <p>维护不安排在考试时段;进行中的考试不受影响,答案已在服务端保存。</p>
          <div class="sx-acts">
            <button type="button" class="ptl-cta brand lg" @click="reload">刷新</button>
          </div>
          <small>维护公告会提前 24 小时在消息中心与官网发布。</small>
        </template>

        <template v-else>
          <h1 :id="`st-${kind}`">加载失败</h1>
          <p>网络不稳定或服务暂时不可用。刚才的筛选条件已保留,重试即可。</p>
          <div class="sx-acts">
            <button type="button" class="ptl-cta brand lg" @click="retry">重试</button>
            <RouterLink class="ptl-cta line lg" :to="{ name: 'site-home' }">回官网</RouterLink>
          </div>
          <small>考试答题页断线不在这里处理:回到考试端即可续考。</small>
        </template>

        <p class="sx-no">
          <span
            >错误编号 <span class="ptl-digits">{{ errorNo }}</span></span
          >
          <button type="button" class="sx-copy" @click="copyNo">复制</button>
        </p>
      </section>

      <nav v-if="demo" class="ptl-pills sx-demo" aria-label="状态演示">
        <button
          v-for="t in TABS"
          :key="t.k"
          type="button"
          class="ptl-pill"
          :class="{ on: kind === t.k }"
          @click="override = t.k"
        >
          {{ t.t }}
        </button>
      </nav>
    </main>
  </div>
</template>

<style scoped>
/* 卡片的层次、按钮、筛选小胶囊是共用的(ptl-*,外面这层 .ptl 让它们生效);这里是整屏的摆法 */
.sx {
  isolation: isolate;
  overflow: hidden;
  background: var(--ptl-tint);
}
/* 底上一团柔和的光(卡片后面),右下再一点点睛的 */
.sx-glow {
  position: absolute;
  inset: 0;
  z-index: -1;
  background:
    radial-gradient(
      680px 440px at 50% 38%,
      color-mix(in srgb, var(--brand) 17%, transparent),
      transparent 70%
    ),
    radial-gradient(
      520px 360px at calc(50% + 420px) 86%,
      color-mix(in srgb, var(--ptl-spark) 13%, transparent),
      transparent 70%
    );
}
.sx-brand {
  flex: none;
  padding: 28px 16px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}
.sx-brand i,
.sx-brand img {
  flex: none;
  width: 38px;
  height: 38px;
  border-radius: 11px;
}
.sx-brand i {
  display: grid;
  place-items: center;
  overflow: hidden;
  background: linear-gradient(180deg, var(--brand), var(--brand-600));
  color: var(--ptl-on);
  box-shadow: 0 8px 16px -8px color-mix(in srgb, var(--brand) 80%, transparent);
  font-style: normal;
  font-size: 19px;
  font-weight: 900;
}
.sx-brand img {
  object-fit: contain;
  background: var(--card);
}
.sx-brand b {
  min-width: 0;
  font-family: var(--ptl-display);
  font-size: 18px;
  font-weight: 800;
  color: var(--ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sx-main {
  flex: 1;
  padding: 104px 16px 72px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
/* 卡片:顶上一抹很淡的主题色;小智从上沿探出来,所以卡片自己不裁边 */
.sx-card {
  width: min(640px, 100%);
  padding: 44px 48px 0;
  overflow: visible;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: linear-gradient(180deg, var(--brand-050), var(--card) 34%);
  box-shadow: var(--ptl-lift-3);
}
.sx-card.has-art {
  padding-top: 0;
}
.sx-art {
  display: block;
  width: 164px;
  height: 184px;
  margin-top: -84px;
  margin-bottom: 14px;
  filter: drop-shadow(0 18px 18px color-mix(in srgb, var(--ink) 18%, transparent));
}
.sx-ico {
  width: 76px;
  height: 76px;
  margin-bottom: 26px;
  display: grid;
  place-items: center;
  border-radius: 23px;
  background: linear-gradient(150deg, var(--card), var(--brand-100));
  color: var(--brand);
  box-shadow:
    0 0 0 1px color-mix(in srgb, var(--brand) 16%, transparent),
    0 16px 28px -14px color-mix(in srgb, var(--brand) 70%, transparent);
}
.sx-ico.is-warm {
  background: linear-gradient(150deg, var(--card), var(--amber-100));
  color: var(--warn);
  box-shadow:
    0 0 0 1px color-mix(in srgb, var(--amber) 30%, transparent),
    0 16px 28px -14px color-mix(in srgb, var(--amber) 80%, transparent);
}
.sx-card h1 {
  font-family: var(--ptl-display);
  font-size: clamp(24px, 3.4vw, 32px);
  font-weight: 800;
  line-height: 1.35;
  color: var(--ink);
  text-wrap: balance;
}
.sx-card > p {
  margin-top: 12px;
  max-width: 30em;
  font-size: 16.5px;
  line-height: 1.8;
  color: var(--ptl-sub);
  text-wrap: pretty;
}
.sx-acts {
  margin-top: 30px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
}
.sx-acts .ptl-cta {
  min-width: 172px;
}
.sx-card small {
  margin-top: 22px;
  font-size: 14px;
  line-height: 1.7;
  color: var(--ptl-sub);
}
/* 底栏:错误编号(等宽数字)和「复制」 */
.sx-card > .sx-no {
  align-self: stretch;
  max-width: none;
  min-height: 52px;
  margin: 36px -48px 0;
  padding: 4px 24px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 2px 12px;
  border-radius: 0 0 var(--ptl-r-3) var(--ptl-r-3);
  background: var(--ptl-tint);
  box-shadow: inset 0 1px 0 var(--ptl-edge);
  font-size: 14px;
  line-height: 1.6;
  color: var(--ptl-sub);
}
.sx-no .ptl-digits {
  margin-left: 4px;
  letter-spacing: 0.04em;
  color: var(--ink-2);
}
.sx-copy {
  min-height: 44px;
  padding: 0 6px;
  border: 0;
  background: none;
  color: var(--brand-600);
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}
.sx-copy:hover {
  text-decoration: underline;
  text-underline-offset: 5px;
}
.sx-copy:focus-visible {
  outline: 2px solid var(--brand);
  outline-offset: 2px;
  border-radius: 6px;
}
.sx-demo {
  margin-top: 28px;
  justify-content: center;
}
/* 公告横幅的文字两边留出 16(它自己的版式是给 1180 的内容列写的) */
@media (max-width: 1212px) {
  .sx :deep(.pnb > .in) {
    padding-left: 16px;
    padding-right: 16px;
  }
}
/* ── 手机:留白收一档,按钮竖着排、通栏 ── */
@media (max-width: 560px) {
  .sx-brand {
    padding-top: 20px;
  }
  .sx-main {
    padding: 96px 16px 48px;
  }
  .sx-card {
    padding: 36px 22px 0;
  }
  .sx-art {
    width: 140px;
    height: 157px;
    margin-top: -72px;
  }
  .sx-card > p {
    font-size: 16px;
  }
  .sx-acts {
    align-self: stretch;
    flex-direction: column;
  }
  .sx-card > .sx-no {
    margin: 30px -22px 0;
  }
}
</style>
